<script lang="ts">
  import { createEventDispatcher, onMount } from "svelte";
  import {
    initializePrivacyConsent,
    privacyConsent,
    privacyConsentEnabled,
    setPrivacyConsent,
  } from "$lib/privacy-consent";
  import { trackPageView } from "$lib/analytics";

  export let initialVisible: boolean | undefined = undefined;

  const dispatch = createEventDispatcher<{ manage: void }>();
  let visible = initialVisible ?? false;

  onMount(() => {
    const state = initializePrivacyConsent();
    if (initialVisible === undefined) {
      visible = privacyConsentEnabled() && state.record === null;
    }
  });

  $: if (initialVisible === undefined) {
    visible = privacyConsentEnabled() && $privacyConsent.initialized && $privacyConsent.record === null;
  }

  const acceptAll = () => {
    setPrivacyConsent({ analytics: "granted", externalMedia: "granted" });
    trackPageView();
    visible = false;
  };

  const rejectAll = () => {
    setPrivacyConsent({ analytics: "denied", externalMedia: "denied" });
    visible = false;
  };
</script>

{#if visible}
  <aside
    class="fixed inset-x-0 bottom-0 z-40 rounded-t-xl bg-white p-3 text-xs leading-snug text-slate-700 shadow-lg sm:inset-x-3 sm:bottom-3 sm:left-auto sm:max-w-xl sm:rounded-xl sm:p-4 sm:text-sm sm:leading-relaxed"
    aria-label="Cookie and browser-storage notice">
    <p class="max-w-xl">We use optional cookies to improve your experience.</p>
    <div class="mt-2 flex flex-wrap gap-2 sm:mt-3">
      <button
        type="button"
        class="min-h-10 flex-1 whitespace-nowrap rounded-full bg-emerald-700 px-2 py-1.5 text-xs font-semibold leading-tight text-white sm:min-h-11 sm:px-3 sm:py-2 sm:text-sm transition hover:bg-emerald-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-700 active:bg-emerald-900 motion-reduce:transition-none sm:px-5"
        on:click={acceptAll}>
        Accept All Cookies
      </button>
      <button
        type="button"
        class="min-h-10 flex-1 whitespace-nowrap rounded-full border border-slate-400 bg-white px-2 py-1.5 text-xs font-semibold leading-tight text-slate-800 sm:min-h-11 sm:px-3 sm:py-2 sm:text-sm transition hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-700 active:bg-slate-100 motion-reduce:transition-none sm:px-5"
        on:click={rejectAll}>
        Reject All
      </button>
    </div>
    <p
      class="mt-1.5 flex flex-wrap items-center justify-center gap-x-2 text-[11px] text-slate-600 sm:mt-2 sm:justify-start sm:text-xs">
      <button
        type="button"
        class="-my-2 py-2 font-semibold text-slate-800 underline decoration-slate-400 underline-offset-4 focus-visible:rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-700"
        on:click={() => dispatch("manage")}>Manage your choices</button>
      <span aria-hidden="true">|</span>
      <a
        class="-my-2 py-2 font-semibold text-slate-800 underline decoration-slate-400 underline-offset-4 focus-visible:rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-700"
        href="https://www.getzero.earth/privacy-policy"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Privacy policy (opens in a new tab)">Privacy policy</a>
    </p>
  </aside>
{/if}
